import JSZip from 'jszip';
import { bytesToGB } from '@iris/common/modules/dms/helpers/dms-files.helper';
import { counter, CounterI } from '@iris/common/utils/counter';
import { fileEntryGetFile, readEntriesAsync } from '@iris/common/utils/file-system-api';
import { FileEntry, Entry, DirectoryEntry, WebkitFileI } from '@iris/common/models/FileSystem';
import { from, of, Observable, lastValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

class ZippingFolder {
  parent: ZippingFolder;
  name: string;
  maxFolderTreeSize: number;
  files: File[] = [];
  children: ZippingFolder[] = [];

  static sizeCounter: CounterI = counter();
  static zip: JSZip = new JSZip();

  static assignChildToParent(child: ZippingFolder, parent: ZippingFolder): void {
    parent.addChildFolder(child);
    child.parent = parent;
  }

  static generateZip(folder: ZippingFolder, parentFolderZip?): Promise<Blob> {
    const folderZip = parentFolderZip
      ? parentFolderZip.folder(folder.name)
      : ZippingFolder.zip.folder(folder.name);

    folder.files.forEach((file) => {
      ZippingFolder.sizeCounter.increment(file.size);
      folderZip.file(file.name, file, { base64: true });
    });

    folder.children.forEach((child) => {
      void ZippingFolder.generateZip(child, folderZip);
    });

    if (!parentFolderZip) {
      const generalSizeGb = bytesToGB(ZippingFolder.sizeCounter.get());

      ZippingFolder.sizeCounter.clear();

      if (generalSizeGb > folder.maxFolderTreeSize) { return lastValueFrom(of(null)); }

      return ZippingFolder.zip.generateAsync({ type: 'blob' });
    }
  }

  constructor(name: string) {
    this.name = name;
  }

  addFile(file: File): ZippingFolder {
    this.files.push(file);
    return this;
  }

  private addChildFolder(folder: ZippingFolder): ZippingFolder {
    if (folder === null) {
      return this;
    }
    this.children.push(folder);
    return this;
  }

  generateZip$(maxFolderTreeSizeInBytes: number): Observable<Blob> {
    this.maxFolderTreeSize = bytesToGB(maxFolderTreeSizeInBytes);
    ZippingFolder.zip = new JSZip();
    return from(ZippingFolder.generateZip(this));
  }
}

export async function traverseFileTree(entryItem: Entry, rootFolder?: ZippingFolder, folderName?: string): Promise<ZippingFolder> {
  const folder = rootFolder || new ZippingFolder(folderName);

  if (entryItem.isFile) {
    const file = await fileEntryGetFile(entryItem as FileEntry);
    folder.addFile(file);
  } else if (entryItem.isDirectory) {
    const childFolder = new ZippingFolder(entryItem.name);

    if (rootFolder) {
      ZippingFolder.assignChildToParent(childFolder, folder);
    }

    const dirReader = (entryItem as DirectoryEntry).createReader();
    const entries = [...await readEntriesAsync(dirReader)];
    const promises = entries
      .map((entry) => traverseFileTree(entry, rootFolder ? childFolder : folder));

    await Promise.all(promises);

    if (!rootFolder) {
      return folder;
    }
  }
}

export function zipFolderTree$(folderTree: DirectoryEntry, maxFolderTreeSizeInBytes: number, folderName?: string): Observable<Blob> {
  return from(traverseFileTree(folderTree, null, folderName)).pipe(
    switchMap(traversed => traversed.generateZip$(maxFolderTreeSizeInBytes)),
  );
}

export function mapUploadingFilesIntoFolderTree(files: WebkitFileI[]): ZippingFolder {
  if (!files || !files.length) {
    return null;
  }

  let folder: ZippingFolder = null;
  files.forEach(file => {
    folder = addFileToFolderTree(file, folder);
  });

  return folder;
}

function addFileToFolderTree(file: WebkitFileI, rootFolder?: ZippingFolder): ZippingFolder {
  if (!file || file.name === '.DS_Store') { return rootFolder; }
  const filePath = file.webkitRelativePath;
  const pathParts = filePath.split('/').slice(0, -1);
  const rootFolderName = pathParts.shift();

  if (!rootFolder) {
    rootFolder = new ZippingFolder(rootFolderName);
  }

  let currentFolder = rootFolder;

  pathParts.forEach((pathPart) => {
    const existingFolder = currentFolder.children.find(child => child.name === pathPart);
    if (!existingFolder) {
      const folder = new ZippingFolder(pathPart);
      currentFolder.children.push(folder);
      folder.parent = currentFolder;
      currentFolder = folder;
    } else {
      currentFolder = existingFolder;
    }
  });

  currentFolder.addFile(file as File);
  return rootFolder;
}
